import React, { useEffect } from 'react';
import { Col, Row } from 'antd/lib/grid';
import { useForm } from 'antd/lib/form/Form';
import { Form, FORM_ITEM_RULES, PHONE_MASK } from '@perxis-auto/forms';
import { Input } from '@perxis/perxis-auto-ui-kit/lib/components/Input';
import { InputMask } from '@perxis/perxis-auto-ui-kit/lib/components/InputMask';
import { Select } from '@perxis/perxis-auto-ui-kit/lib/components/Select';
import { Button } from '@perxis/perxis-auto-ui-kit/lib/components/Button';
import { Checkbox } from '@perxis/perxis-auto-ui-kit/lib/components/Checkbox';
import { Icon } from '@perxis/perxis-auto-ui-kit/lib/components/Icon';
import { Typography } from '@perxis/perxis-auto-ui-kit/lib/components/Typography';
import { FormItem } from '@perxis/perxis-auto-ui-kit/lib/components/FormItem';

import { TCreditPayload } from '@/apps/credit-form/types';
import { EVENTS_CHANNEL } from '@/apps/credit-form/constants/events';
import { sendCredit } from '@/api/formService';
import { useDealerDataFetch } from '@/hooks';
import config from '@/utils/config';
import { formatPhone, isExist } from '@/utils/common';
import { TERMS_CONDITIONS_URL } from '@/constants/links';
import { STATUS_MESSAGES } from '@/constants/common';
import { FORM_EVENTS, GEO_LOCATOR_CITY_SELECTED_EVENT } from '@/constants/events';
import { TSelectLabelInValue } from '@/types/common';
import ThankStep from '@/components/ThankStep';

import style from './style.module.scss';

type CreditFormProps = {
  details: TCreditPayload;
};

export const CreditFormFields: Record<keyof CreditFormValues, string> = {
  last_name: 'last_name',
  first_name: 'first_name',
  phone: 'phone',
  email: 'email',
  city: 'city',
  dealer: 'dealer',
  confirm_personal_data_processing_policy: 'confirm_personal_data_processing_policy',
};

export type CreditFormValues = {
  last_name: string;
  first_name: string;
  email: string;
  phone: string;
  city: string | null;
  dealer: TSelectLabelInValue<number> | null;
  confirm_personal_data_processing_policy: boolean;
};

const initialValues: CreditFormValues = {
  last_name: '',
  first_name: '',
  email: '',
  phone: '',
  city: null,
  dealer: null,
  confirm_personal_data_processing_policy: false,
};

const formatFinancialProgram = ({ program, parameters }: TCreditPayload) => {
  const { title, bank, payment, conditions } = program;
  const { downPayment, residualPayment, numberOfPeriods } = parameters;
  return {
    title,
    bank,
    payment: Number(payment),
    rate: conditions.rate,
    down_payment: downPayment,
    number_of_periods: numberOfPeriods,
    residual_payment: residualPayment,
  };
};

const CreditForm: React.VFC<CreditFormProps> = ({ details }: CreditFormProps) => {
  const [form] = useForm<CreditFormValues>();

  const {
    data: { cities },
    loading: dealerLoading,
    getDealersByCity,
  } = useDealerDataFetch();

  const onFinish = async (values: any) => {
    await sendCredit({
      ...values,
      phone: formatPhone(values?.phone),
      credit_data: formatFinancialProgram(details),
    });
  };

  const getGeoLocatorValues = (city: string, resetValues = false) => {
    const isExistCity = isExist(city, cities);
    if (!city || !isExistCity) {
      return resetValues
        ? {
            city: null,
            dealer: null,
          }
        : {};
    }
    const filteredDealers = getDealersByCity(city);
    const isSingleDealer = filteredDealers.length === 1;
    return {
      city,
      dealer: isSingleDealer
        ? {
            value: filteredDealers[0].id,
            label: filteredDealers[0].title,
          }
        : null,
    };
  };

  const geoLocatorEventsHandler = (eventType: string, eventData: any) => {
    switch (eventType) {
      case GEO_LOCATOR_CITY_SELECTED_EVENT: {
        const city = eventData?.city;
        return getGeoLocatorValues(city);
      }
      default:
        return {};
    }
  };

  useEffect(() => {
    if (!dealerLoading) {
      const geoValues = getGeoLocatorValues(form.getFieldValue('city'), true);
      form.setFieldsValue(geoValues);
    }
  }, [dealerLoading]);

  return (
    <Form<CreditFormValues>
      form={form}
      formEvents={FORM_EVENTS}
      formChannel={EVENTS_CHANNEL}
      initialValues={{
        ...initialValues,
        ...config.get('initialValues'),
      }}
      onFinish={onFinish}
      className={style.form}
      finalStep={() => <ThankStep />}
      geoLocatorEventsHandler={geoLocatorEventsHandler}
      successCallback={config.get('successCallback')}
      errorCallback={config.get('errorCallback')}
      formatValues={config.get('formatValues')}
      defaultMessages={STATUS_MESSAGES}
    >
      {({ isSubmitting, values, changeFieldValue, status }) => {
        const { city, dealer } = values ?? {};
        const filteredDealers = getDealersByCity(city);

        const showErrorMessage = status?.type === 'error' && !!status.message;

        return (
          <>
            <Typography.Title className={style.title} level={3} classLevel={1}>
              заявка на кредит
            </Typography.Title>
            <Row
              gutter={[
                { sm: 40, xs: 0 },
                { sm: 30, xs: 20 },
              ]}
            >
              <Col sm={12} xs={24}>
                <FormItem
                  label="Имя"
                  name={CreditFormFields.first_name}
                  rules={[
                    FORM_ITEM_RULES.required,
                    FORM_ITEM_RULES.whitespace,
                    FORM_ITEM_RULES.minLength(2),
                    FORM_ITEM_RULES.maxLength(25),
                  ]}
                >
                  <Input />
                </FormItem>
              </Col>
              <Col sm={12} xs={24}>
                <FormItem
                  label="Фамилия"
                  name={CreditFormFields.last_name}
                  rules={[
                    FORM_ITEM_RULES.required,
                    FORM_ITEM_RULES.whitespace,
                    FORM_ITEM_RULES.minLength(2),
                    FORM_ITEM_RULES.maxLength(25),
                  ]}
                >
                  <Input />
                </FormItem>
              </Col>
              <Col sm={12} xs={24}>
                <FormItem
                  label="Телефон"
                  name={CreditFormFields.phone}
                  rules={[
                    FORM_ITEM_RULES.required,
                    FORM_ITEM_RULES.whitespace,
                    FORM_ITEM_RULES.phone,
                    FORM_ITEM_RULES.maxLength(20),
                  ]}
                >
                  <InputMask
                    type="tel"
                    mask={PHONE_MASK}
                    maskOptions={{
                      lazy: true,
                      placeholderChar: 'X',
                    }}
                  />
                </FormItem>
              </Col>
              <Col sm={12} xs={24}>
                <FormItem
                  label="E-mail"
                  name={CreditFormFields.email}
                  rules={[
                    FORM_ITEM_RULES.required,
                    FORM_ITEM_RULES.whitespace,
                    FORM_ITEM_RULES.email,
                    FORM_ITEM_RULES.maxLength(255),
                  ]}
                >
                  <Input />
                </FormItem>
              </Col>
              <Col sm={12} xs={24}>
                <FormItem
                  label="Город"
                  name={CreditFormFields.city}
                  rules={[FORM_ITEM_RULES.required]}
                  loading={dealerLoading}
                >
                  <Select
                    disabled={dealerLoading}
                    loading={dealerLoading}
                    onChange={city => {
                      if (dealer) changeFieldValue({ dealer: null });

                      if (city) {
                        const filteredDealers = getDealersByCity(city);
                        filteredDealers.length === 1 &&
                          changeFieldValue({
                            dealer: {
                              value: filteredDealers[0].id,
                              label: filteredDealers[0].title,
                            },
                          });
                      }
                    }}
                  >
                    {cities.map(city => (
                      <Select.Option key={city} value={city}>
                        {city}
                      </Select.Option>
                    ))}
                  </Select>
                </FormItem>
              </Col>
              <Col sm={12} xs={24}>
                <FormItem
                  label="Дилер"
                  name={CreditFormFields.dealer}
                  rules={[FORM_ITEM_RULES.required]}
                  loading={dealerLoading}
                >
                  <Select
                    disabled={!city || dealerLoading}
                    loading={dealerLoading}
                    labelInValue
                  >
                    {filteredDealers.map(({ id, title }) => (
                      <Select.Option key={id} value={id}>
                        {title}
                      </Select.Option>
                    ))}
                  </Select>
                </FormItem>
              </Col>
              <Col span={24}>
                <FormItem
                  name={CreditFormFields.confirm_personal_data_processing_policy}
                  rules={[FORM_ITEM_RULES.required, FORM_ITEM_RULES.term]}
                  valuePropName="checked"
                  className={style.checkboxField}
                >
                  <Checkbox>
                    Даю согласие на{' '}
                    <Typography.Link
                      href={TERMS_CONDITIONS_URL}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      обработку своих персональных данных
                    </Typography.Link>
                    *
                  </Checkbox>
                </FormItem>
              </Col>
            </Row>
            {showErrorMessage && (
              <Typography.Paragraph className={style.errorMessage}>
                {status.message}
              </Typography.Paragraph>
            )}
            <div className={style.footer}>
              <Button
                type="primary"
                htmlType="submit"
                disabled={isSubmitting}
                icon={<Icon name="arrow-1" width={16} height={16} />}
              >
                отправить
              </Button>
              <Typography.Text className={style.explainRequired}>
                * Все поля, отмеченные звездочкой, являются обязательными для заполнения.
              </Typography.Text>
            </div>
          </>
        );
      }}
    </Form>
  );
};

export default CreditForm;
